<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import {useClaimStore} from "@/stores/Claim.js";
import useSharedMethod from "@/use/SharedMethod.js";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    name: "ClaimFormModel",
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        type: {
            type: String,
            required: true,
        }
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack, getClaimParamsByType} = useSharedMethod();

        const claimStore = useClaimStore();

        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });

        const claim = ref({
            type: props.type,
        });

        const params = getClaimParamsByType(props.type);


        function findById(id) {
            loading.value = true;
            axios.get(`/${params.api}/${id}`).then((response) => {
                claim.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post(`/${params.api}`, claim.value).then((response) => {
                    claimStore.addClaim(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put(`/${params.api}`, claim.value).then((response) => {
                    claimStore.updateClaim(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/${params.api}/${claim.value.id}`).then(() => {
                claimStore.deleteClaim(claim.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            claim,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog v-model="dialog" persistent max-width="700" scrollable>
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>

            <v-card-text>
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="claim.code"
                                        :entity="type"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="claim.workerId"
                                        :action="`SAVE_${type}`"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('common.date')" required/>
                                    <w-date-picker
                                        v-model="claim.date"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('claim.target')" required/>
                                    <w-data-select-autocomplete
                                        v-model="claim.targetId"
                                        :type="`${type}_TARGET`"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-data-select-autocomplete>
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.designation')" required/>
                                    <w-simple-text-area
                                        v-model="claim.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-simple-text-area>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>